import React, { useState } from 'react';
import { Search as SearchIcon, MapPin, PersonStanding, Car, Bike, Bus, List } from 'lucide-react';
import { type LocationSearchResult } from '@/types/locationSearch';
import { type PlaceSearchCountry } from '@/utils/placeSearchNotice';
import { searchPlacesInCountry } from '@/utils/placeLocationSearch';
import {
  formatRouteDistance,
  resolveTravelMode,
  routeDistanceMeters,
  shouldOfferWalk,
  type GoogleTravelMode,
} from '@/utils/googleDirectionsLink';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import GoogleMap from '@/components/GoogleMap';
import ShowDirectionDialog from '@/components/search/ShowDirectionDialog';

const TRAVEL_CHOICES: { mode: GoogleTravelMode; label: string; Icon: typeof Car }[] = [
  { mode: 'walking', label: 'Walk', Icon: PersonStanding },
  { mode: 'driving', label: 'Drive', Icon: Car },
  { mode: 'bicycling', label: 'Bike', Icon: Bike },
  { mode: 'transit', label: 'Transit', Icon: Bus },
];

interface DirectionsPlaceFieldProps {
  id: string;
  label: string;
  countries: PlaceSearchCountry[];
  detectedCountry: PlaceSearchCountry | null;
  selectedCountry: PlaceSearchCountry | null;
  onDetectedCountry: (country: PlaceSearchCountry) => void;
  onPlaceChange: (place: LocationSearchResult | null) => void;
  /** Rendered under this field's map card. Destination uses it for distance and travel. */
  afterCard?: React.ReactNode;
}

/**
 * Place search for one end of a route.
 * Hits stay listed until the user picks one. That choice hides the others and leaves the map card.
 */
function DirectionsPlaceField({
  id,
  label,
  countries,
  detectedCountry,
  selectedCountry,
  onDetectedCountry,
  onPlaceChange,
  afterCard,
}: DirectionsPlaceFieldProps) {
  const [query, setQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [selected, setSelected] = useState<LocationSearchResult | null>(null);
  const [hits, setHits] = useState<LocationSearchResult[]>([]);

  const choosePlace = (place: LocationSearchResult) => {
    setSelected(place);
    setHits([]);
    setNotice(null);
    setError(null);
    onPlaceChange(place);
  };

  const handleQueryChange = (value: string) => {
    setQuery(value);
    setNotice(null);
    setError(null);
    if (selected || hits.length > 0) {
      setSelected(null);
      setHits([]);
      onPlaceChange(null);
    }
  };

  const runSearch = async () => {
    const trimmed = query.trim();
    if (trimmed.length <= 2) {
      return;
    }
    setLoading(true);
    setNotice(null);
    setError(null);
    setSelected(null);
    setHits([]);
    onPlaceChange(null);
    try {
      const outcome = await searchPlacesInCountry(trimmed, countries, detectedCountry, selectedCountry);
      if (outcome.detectedCountry?.code && outcome.detectedCountry?.name) {
        onDetectedCountry(outcome.detectedCountry);
      }
      if (outcome.notice) {
        setNotice(outcome.notice);
        return;
      }
      if (outcome.error || !outcome.primary) {
        setError(outcome.error || 'Location not found');
        return;
      }
      setHits([outcome.primary, ...outcome.alternatives]);
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to find location';
      setError(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-2">
      <Label htmlFor={id} className="text-sm font-medium text-gray-800">
        {label}
      </Label>
      <div className="search-input-container w-full">
        <div className="search-icon-container">
          <SearchIcon className="h-5 w-5 text-gray-400" />
        </div>
        <input
          id={id}
          type="text"
          value={query}
          placeholder="Place name or nearby category — press Enter"
          autoComplete="off"
          className="search-input-field"
          aria-label={`${label} place`}
          onChange={(event) => handleQueryChange(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === 'Enter') {
              event.preventDefault();
              void runSearch();
            }
          }}
        />
        <div className="search-spinner-container">
          <div
            className={cn(
              'rounded-full h-4 w-4 border-b-2 border-emerald-600 transition-opacity duration-200',
              loading ? 'animate-spin opacity-100' : 'opacity-0'
            )}
          />
        </div>
      </div>

      {query.trim().length > 0 && query.trim().length < 3 && !selected && hits.length === 0 && (
        <p className="text-xs sm:text-sm text-gray-500">
          Type 3+ characters and press <span className="font-medium">Enter</span> to search places in your country
        </p>
      )}

      {query.trim().length >= 3 && !selected && hits.length === 0 && !loading && !notice && !error && (
        <p className="text-sm text-gray-600">
          A place name is searched across your country, closest first.
          A category such as coffee or pharmacy stays near you.
          Press <span className="font-medium">Enter</span> to search.
        </p>
      )}

      {notice && <p className="text-sm text-amber-800">{notice}</p>}
      {error && <p className="text-sm text-red-600">{error}</p>}

      {!selected && hits.length > 0 && (
        <div className="rounded-lg border border-gray-200 bg-white">
          {hits.map((place) => (
            <button
              key={place.placeId || place.formattedAddress}
              type="button"
              className="w-full px-3 py-2 text-left hover:bg-blue-50 border-b border-gray-50 last:border-b-0"
              onClick={() => choosePlace(place)}
            >
              <span className="block text-sm font-medium text-gray-900">{place.name || place.formattedAddress}</span>
              {place.name && (
                <span className="block text-xs text-gray-600">{place.formattedAddress}</span>
              )}
            </button>
          ))}
        </div>
      )}

      {selected && (
        <div className="rounded-lg border border-gray-200 bg-white p-4 space-y-3">
          <div className="flex items-start gap-3">
            <MapPin className="h-5 w-5 text-blue-600 mt-0.5 shrink-0" />
            <div>
              {selected.name && (
                <p className="text-lg font-bold text-blue-900">{selected.name}</p>
              )}
              <p className="text-sm text-gray-700">
                <span className="font-bold text-blue-900">Location:</span> {selected.formattedAddress}
              </p>
            </div>
          </div>
          <div className="h-64 rounded-lg overflow-hidden border border-gray-300">
            <GoogleMap
              key={selected.placeId}
              latitude={selected.lat}
              longitude={selected.lng}
              address={selected.name || selected.formattedAddress}
              className="w-full h-full"
            />
          </div>
        </div>
      )}

      {selected ? afterCard : null}
    </div>
  );
}

interface GetDirectionsPanelProps {
  countries: PlaceSearchCountry[];
  detectedCountry: PlaceSearchCountry | null;
  selectedCountry: PlaceSearchCountry | null;
  onDetectedCountry: (country: PlaceSearchCountry) => void;
}

/**
 * Origin and destination, then Show Direction, the distance, and Walk / Drive / Bike / Transit.
 */
const GetDirectionsPanel = ({
  countries,
  detectedCountry,
  selectedCountry,
  onDetectedCountry,
}: GetDirectionsPanelProps) => {
  const [origin, setOrigin] = useState<LocationSearchResult | null>(null);
  const [destination, setDestination] = useState<LocationSearchResult | null>(null);
  const [chosenMode, setChosenMode] = useState<GoogleTravelMode | null>(null);
  const [stepsOpen, setStepsOpen] = useState(false);

  const distanceMeters =
    origin && destination ? routeDistanceMeters(origin, destination) : null;
  const travelChoices =
    distanceMeters == null
      ? TRAVEL_CHOICES
      : TRAVEL_CHOICES.filter((choice) => choice.mode !== 'walking' || shouldOfferWalk(distanceMeters));
  const mode = resolveTravelMode(chosenMode, distanceMeters);

  // Walk, Drive, Bike, and Transit open the in-app Google route.
  // window.open on that Maps URL leaves the Capacitor app for the system browser.
  const openTravel = (next: GoogleTravelMode) => {
    if (!origin || !destination) return;
    setChosenMode(next);
    setStepsOpen(true);
  };

  const routeSummary =
    origin && destination && distanceMeters != null ? (
      <div className="space-y-3 pt-1">
        <Button
          type="button"
          className="bg-blue-600 hover:bg-blue-700 text-white"
          onClick={() => setStepsOpen(true)}
        >
          <List className="h-4 w-4 mr-2" />
          Show Direction
        </Button>
        <p className="text-sm font-medium text-gray-800">
          Distance: {formatRouteDistance(distanceMeters)}
        </p>
        <div className="flex flex-wrap gap-2">
          {travelChoices.map((choice) => (
            <Button
              key={choice.mode}
              type="button"
              variant={chosenMode === choice.mode ? 'default' : 'outline'}
              size="sm"
              className="flex items-center space-x-2"
              onClick={() => openTravel(choice.mode)}
            >
              <choice.Icon className="h-4 w-4" />
              <span>{choice.label}</span>
            </Button>
          ))}
        </div>
      </div>
    ) : null;

  return (
    <div className="space-y-5">
      <DirectionsPlaceField
        id="directions-origin"
        label="Origin"
        countries={countries}
        detectedCountry={detectedCountry}
        selectedCountry={selectedCountry}
        onDetectedCountry={onDetectedCountry}
        onPlaceChange={setOrigin}
      />
      <DirectionsPlaceField
        id="directions-destination"
        label="Destination"
        countries={countries}
        detectedCountry={detectedCountry}
        selectedCountry={selectedCountry}
        onDetectedCountry={onDetectedCountry}
        onPlaceChange={setDestination}
        afterCard={routeSummary}
      />
      <ShowDirectionDialog
        open={stepsOpen}
        onOpenChange={setStepsOpen}
        origin={origin ? { lat: origin.lat, lng: origin.lng } : null}
        destination={destination ? { lat: destination.lat, lng: destination.lng } : null}
        mode={mode}
      />
    </div>
  );
};

export default GetDirectionsPanel;
